// Authoritative game constants. Client and server both import from here so
// physics, bounds, and timing never drift between them.
//
// Mirrored to C# at `desktop/Assets/Scripts/Protocol/Constants.cs` for the
// Unity desktop client. Values that affect the wire (TILE_SIZE, MAP_WIDTH,
// TICK_RATE_HZ, MATCH_DURATION_MS, MAX_TEAM, etc.) must be kept in lockstep
// across both. Values that only affect server-side simulation can be tuned
// here without touching the C# mirror — but keeping them in sync is still a
// good habit so any client-side prediction/interpolation stays accurate.

// World geometry. The original WinBolo map is 256x256 tiles, each tile
// rendered at 16x16 pixels in the classic client. We keep the same grid so
// .map files remain loadable unchanged.
export const MAP_WIDTH = 256;
export const MAP_HEIGHT = 256;
export const TILE_SIZE = 16;
export const WORLD_WIDTH_PX = MAP_WIDTH * TILE_SIZE;
export const WORLD_HEIGHT_PX = MAP_HEIGHT * TILE_SIZE;

// Simulation. WinBolo's net tick was ~14 Hz; we run authoritative sim at
// 20 Hz for smoother web play and interpolate on the client.
export const TICK_RATE_HZ = 20;
export const TICK_MS = 1000 / TICK_RATE_HZ;

// Match limits.
export const MAX_PLAYERS_PER_GAME = 16;
export const USERNAME_MIN = 3;
// Hard cap on new usernames. Anything longer than this would overflow the
// leaderboard's name column and push the lobby panel past its grid track.
// Legacy accounts created before this limit may exceed it; the leaderboard
// filters those out so a long-named user can't visually break the table.
export const USERNAME_MAX = 12;
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

// Tank direction is encoded as 16 compass steps (0 = N, rotates CW).
export const TANK_DIRECTIONS = 16;

// Starting inventory for a fresh tank.
export const TANK_START_SHELLS = 40;
export const TANK_START_MINES = 0;
export const TANK_START_TREES = 0;
export const TANK_START_ARMOR = 40;
export const TANK_MAX_SHELLS = 40;
export const TANK_MAX_MINES = 40;
export const TANK_MAX_TREES = 40;
export const TANK_MAX_ARMOR = 40;

// ---------- Physics ----------
// Speed values calibrated to WinBolo's source.
//
// WinBolo runs at 50 fps and expresses speed in WORLD units / tick where
// 1 tile = 256 wu and 1 tile = 16 px (so 1 px = 16 wu). To convert a
// WinBolo wu/tick value to our px/tick at 20 Hz:
//   px/our_tick = wu/wb_tick * (50 / 20) / 16 = wu/wb_tick * 0.15625
//
// Source citations: tank.h doc block (max tank speed = 16 wu/tick),
// shells.h:52 (SHELL_SPEED = 32 wu/tick), tank.h:96 (TANK_ACCELERATE_RATE
// = 0.25 wu/half-tick = 0.25 wu/tick effectively), tank.h:123
// (TANK_RELOAD_TIME = 13 ticks @ 50 fps ≈ 260 ms).
export const TANK_MAX_SPEED = 2.5;           // 16 wu/tick @ 50 fps -> 2.5 px/tick @ 20 Hz
export const TANK_ACCEL = 0.2;               // 0.25 wu/wb_tick scaled
export const TANK_BRAKE = 0.5;               // unchanged: brake is harsher than accel in original
export const TANK_FRICTION = 0.15;
export const TANK_TURN_COOLDOWN_TICKS = 6;   // ticks per direction step. WinBolo: 16 bradians/step ÷ 1.0 bradian/wb-tick = 16 wb-ticks = 320 ms. At 20 Hz: 320/50 = 6.4 → 6 ticks.
export const TANK_RADIUS = 6;                // collision radius (px) for our movement broadphase

// Lane-assist: when a tank drives along a road tile with its heading closely
// aligned to a cardinal axis, gently pull it toward the lane centerline so it
// doesn't clip wall/water corners on the 2px-of-slack-per-side road grid. The
// alignment gate means any deliberate turn instantly disengages the assist, so
// leaving the road is no harder than before.
export const LANE_ASSIST_ALIGN = 0.95;       // |cos θ| or |sin θ| above this counts as "driving straight" (~18° cone)
export const LANE_ASSIST_MAX_PULL = 0.35;    // px/tick cap on the centerline nudge
export const LANE_ASSIST_GAIN = 0.25;        // proportional pull factor on centerline offset
export const LANE_ASSIST_MIN_SPEED = 0.5;    // px/tick: only assist once the tank is actually rolling

export const SHELL_SPEED = 5.0;              // 32 wu/tick @ 50 fps -> 5 px/tick @ 20 Hz
export const SHELL_RADIUS = 2;
export const SHELL_DAMAGE = 5;               // DAMAGE in global.h:236
// TANK_RELOAD_TIME 13 wb ticks * (20/50) ≈ 5 of our ticks.
export const TANK_FIRE_COOLDOWN_TICKS = 5;
// Gunsight range. WinBolo tank.h:119-120. Measured in half-tiles; divide
// by 2 to get full-tile range. At GUNSIGHT_MAX=14 the shell travels 7 tiles
// (≈20 ticks at our 20 Hz). At GUNSIGHT_MIN=2 it travels 1 tile (≈1 tick).
// Adjust cooldown mirrors WinBolo's INPUT_GUNSIGHT_WAIT_TIME = 6 wb-ticks
// (120 ms) → 2-3 of our ticks.
export const GUNSIGHT_MIN = 2;
export const GUNSIGHT_MAX = 14;
export const GUNSIGHT_COOLDOWN_TICKS = 2;
// Minimum tank speed for a tank-on-boat to disembark onto land.
// tank.h:146 BOAT_EXIT_SPEED = 16 wu/tick, which is the same as max tank
// speed. We mirror by requiring the tank's current speed magnitude to be
// at TANK_MAX_SPEED * BOAT_EXIT_SPEED_FRACTION before disembarking.
export const BOAT_EXIT_SPEED_FRACTION = 1.0;

// Minimum speed (as a fraction of max) at which slamming into a wall or
// pillbox bounces the tank back to the centre of the previous tile.
// Below this, the tank just grinds to a halt with the regular wall penalty.
export const TANK_BOUNCE_SPEED_FRACTION = 0.5;

// Forest blocks shells. WinBolo shells.c:610-619 (FOREST case): a shell
// that reaches a Forest tile is consumed and the tile flips to Grass
// (Grass+mine if a mine was on it). No pass-through.

// ---------- Terrain erosion ----------
// WinBolo grass.h:43 / swamp.h:43 / rubble.h:43 — each shellable terrain
// type carries a small "life" counter that counts down with every shell
// that lands on the tile. When life hits 0, the tile flips:
//   GRASS_LIFE  = 4 hits → SWAMP   (grass.h:43, GRASS_DEATH_RETURN = SWAMP)
//   SWAMP_LIFE  = 3 hits → RIVER   (swamp.h:43, SWAMP_DEATH_RETURN = RIVER)
//   RUBBLE_LIFE = 4 hits → RIVER   (rubble.h:43)
// Webolo applies these by counting shell-tile entries during flight; the
// counter is reused from world.wallDamage (already parallel to terrain).
export const GRASS_LIFE = 4;
export const SWAMP_LIFE = 3;
export const RUBBLE_LIFE = 4;

// ---------- LGM ----------
// lgm.c:1346 lgmDeathCheck — when an explosion (mine, shell, tank death)
// lands within MAP_SQUARE_MIDDLE (= 128 wu = half a tile = 8 px) of the
// builder, the builder is killed. Solid-tile rule from the original
// (lgm.c:1348) is omitted: in Webolo the builder can't stand on walls /
// pillboxes / bases at all, so the "same-tile required" branch never
// triggers and the distance test is the only one that matters.
export const LGM_MINE_KILL_RADIUS_PX = TILE_SIZE * 0.5;

// ---------- Tank death ----------
// tank.h:65 TANK_DEATH_WAIT = 255 wb-ticks @50fps = 5.1s before respawn
// (covers the explosion animation + TV-static intro). At 20Hz that's 102
// ticks; we round to 100 to keep the wall clock at a clean 5 seconds.
export const TANK_RESPAWN_TICKS = 100;
// WinBolo: when a tank dies and (shells + mines) > TANK_BIG_EXPLOSION_THRESHOLD,
// the death animation flies along the tank's last heading creating craters
// and damaging pillboxes in its path. tank.h:129 / tankexp.c:455.
export const TANK_BIG_EXPLOSION_THRESHOLD = 20;
// Damage dealt to pillboxes hit by the death explosion's crater path.
// tankexp.h:47 (TK_DAMAGE = 5).
export const TANK_DEATH_PILLBOX_DAMAGE = 5;
// How many tiles ahead of the tank's last position the explosion craters.
// In source, BigExplosion checks 4 tiles around (mx, my) in the move dir;
// we expand along the move direction for a more visible "pushback" trail.
export const TANK_DEATH_CRATER_TILES = 4;

// ---------- Pillboxes ----------
// Match WinBolo's pillbox.h. WinBolo runs at 50 fps; we run at 20 Hz, so
// per-tick counters scale by 20/50 = 0.4 to preserve wall-clock feel.
export const PILLBOX_MAX_ARMOR = 15;                      // PILLS_MAX_ARMOUR (pillbox.h:54)
export const PILLBOX_RANGE_PX = Math.round(8.5 * TILE_SIZE); // PILLBOX_FIRE_DISTANCE 8.5 tiles (pillbox.h:48)
// Per-pillbox state defaults. WinBolo: PILLBOX_ATTACK_NORMAL = 100 ticks
// @50fps = 2.0s. At 20Hz that's 40 ticks for the same wall-clock interval.
export const PILLBOX_ATTACK_DEFAULT = 40;
// WinBolo: PILLBOX_MAX_FIRERATE = 6 ticks @50fps = 120 ms. At 20Hz: 2-3 ticks.
export const PILLBOX_ATTACK_MIN = 3;
// pillbox.h:67 MAX_AIM_ITERATE = 200. Used as the per-fire-attempt cap.
export const PILLBOX_MAX_AIM_ITERATIONS = 200;
export const PILLBOX_DAMAGE_PER_SHELL = 1;        // armor lost per hit (PILLS_INCOMING_DAMAGE)
// pillbox.h PILLBOX_COOLDOWN_TIME = 32 wb-ticks (640 ms). Scaled to 20 Hz.
export const PILLBOX_ANGER_COOLDOWN_TICKS = 13;
// pillbox.c:1244-1263 pillsBaseHit — when an allied base is hit by an
// enemy, every pillbox within PILL_BASE_HIT_* (±9 map squares) gets angry
// (same effect as taking a hit: halve attack, set cooldown). pillbox.h.
export const PILLBOX_BASE_HIT_RADIUS_TILES = 9;
// pillbox.c:351 — pillbox can't see a tank fully hidden in trees unless
// within MIN_TREEHIDE_DIST = 768 wu = 3 map squares (tank.h:149) OR the
// tank just fired. Below this distance the pillbox sees through trees.
export const PILLBOX_TREEHIDE_TILES = 3;
// pillbox.c:1013 — repairing a pillbox by driving over it with trees
// converts trees → armour at this multiplier. PILL_REPAIR_AMOUNT = 4
// (pillbox.h). 1 tree spent = +4 armour.
export const PILLBOX_REPAIR_PER_TREE = 4;

// ---------- Bases ----------
export const BASE_MAX_ARMOR = 90;
export const BASE_MAX_SHELLS = 90;
export const BASE_MAX_MINES = 90;
// bases.h:58 BASE_MIN_ARMOUR = 10. The base keeps this much armour in
// reserve and won't give it away, preventing a friendly base from being
// drained to the capturable threshold (BASE_DEAD_ARMOR = 9).
export const BASE_MIN_ARMOR = 10;                  // BASE_MIN_ARMOUR (bases.h:58)
export const BASE_REFUEL_RANGE_PX = 1.5 * TILE_SIZE;
// bases.h refuel cooldowns per resource type (bases.c basesHalfTickCalulator).
// WinBolo uses separate timers: armor takes longer per cycle than shells/mines.
//   BASE_REFUEL_ARMOUR = 46 wb-ticks @50fps → 18 ticks @20Hz  (+5 armour)
//   BASE_REFUEL_SHELLS = 7.5 wb-ticks avg  →  3 ticks @20Hz  (+1 shell)
//   BASE_REFUEL_MINES  = 7.5 wb-ticks avg  →  3 ticks @20Hz  (+1 mine)
// Priority: armour > shells > mines (bases.c:713-740).
export const BASE_REFUEL_COOLDOWN_TICKS = 18;        // armor  BASE_REFUEL_ARMOUR 46 wb-ticks
export const BASE_REFUEL_SHELLS_COOLDOWN_TICKS = 3;  // shells BASE_REFUEL_SHELLS 7.5 wb-ticks
export const BASE_REFUEL_MINES_COOLDOWN_TICKS = 3;   // mines  BASE_REFUEL_MINES  7.5 wb-ticks
export const BASE_REFUEL_ARMOR_GIVE = 5;           // BASE_ARMOUR_GIVE (bases.h:64)
export const BASE_REFUEL_SHELLS_GIVE = 1;          // BASE_SHELLS_GIVE (bases.h:63)
export const BASE_REFUEL_MINES_GIVE = 1;           // BASE_MINES_GIVE (bases.h:65)
// bases.h:37 BASE_TICKS_BETWEEN_REFUEL = 1000 wb-ticks @50fps = 20s per +1.
// This is the current winbolo algorithm (the old BASE_ADD_TIME = 1670 was
// replaced). At 20Hz that's 400 ticks. All bases regen including dead/
// capturable ones so a neglected neutral base can slowly heal itself.
export const BASE_REGEN_COOLDOWN_TICKS = 400;
// bases.h:48 BASE_DEAD = 9 armour. A base falls to "dead/capturable" state
// when armour <= 9 (not 0). Tank requires armour >= MIN_ARMOUR_CAPTURE = 9
// to take ownership.
export const BASE_DEAD_ARMOR = 9;
export const MIN_ARMOR_FOR_CAPTURE = 9;            // bases.h:72 MIN_ARMOUR_CAPTURE

// ---------- Tree regrowth ----------
// Direct port of the constants in winbolo/src/bolo/treegrow.h.
// WinBolo runs at 50 fps; we run at 20 Hz. Times scale by (50/20) so the
// wall-clock cadence is preserved.
//   TREEGROW_TIME 3000 wb-ticks = 60 s -> 1200 of our ticks
//   TREEGROW_INITIAL_TIME 30000 wb-ticks = 10 min -> 12000 of our ticks
export const TREEGROW_TIME_TICKS = 1200;
export const TREEGROW_INITIAL_TICKS = 12000;
// SIEGE-only overrides. Waves of attackers actively destroy forest and a
// 2-minute round at the classic rate (~1 tree/min) leaves the map bare
// within a few rounds. Drop the plant cadence to ~2 s per tree (≈30
// trees/min) so the forest survives sustained bombardment. Use the same
// value for INITIAL so the very first tree shows up immediately on a
// freshly-restarted siege run.
export const SIEGE_TREEGROW_TIME_TICKS = 40;
export const SIEGE_TREEGROW_INITIAL_TICKS = 40;
export const TREE_NO_GROW = 0;
export const TREEGROW_INITIAL_SCORE = -10000;
// Per-neighbour score contributions (treegrow.h:45-56). Sum of 9 cells
// (the candidate + its 8 neighbours) determines whether a tile gets a
// new tree.
export const TREE_GROW_FOREST = 100;
export const TREE_GROW_GRASS = 25;
export const TREE_GROW_RIVER = 2;
export const TREE_GROW_BOAT = 1;
export const TREE_GROW_DEEP_SEA = 0;
export const TREE_GROW_SWAMP = 2;
export const TREE_GROW_RUBBLE = -2;
export const TREE_GROW_BUILDING = -20;
export const TREE_GROW_HALF_BUILDING = -15;
export const TREE_GROW_CRATER = -2;
export const TREE_GROW_ROAD = -100;
export const TREE_GROW_MINE = -7;

// ---------- Water exposure ("drowning") ----------
// WinBolo's tank.h:126 TANK_WATER_TIME = 15 ticks @ 50 fps. Tank in
// river without onBoat loses 1 shell + 1 mine each interval (tank.c:1591
// + tank.c:1012 tankInWater). Inventory drain, not armor damage.
// At 20 Hz that's 6 ticks ≈ 300 ms.
export const TANK_WATER_TICKS = 6;

// ---------- Tank slide on shell hit ----------
// tank.h:55-57. When a tank takes a non-fatal shell hit, it slides in
// the shell's direction for TANK_SLIDE_TICKS ticks at TANK_SLIDE wu/tick.
// Wb runs at 50 fps; at our 20 Hz the slide ticks compress.
// 16 wu/wb-tick * (50/20)/16 = 2.5 px/our-tick. 7 wb-ticks * (20/50) ≈ 3
// of our ticks for the same wall-clock duration.
export const TANK_SLIDE_PX_PER_TICK = 2.5;
export const TANK_SLIDE_TICKS = 3;

// ---------- Mines ----------
// MINE_DAMAGE matches WinBolo's tank.h:132 (= 10). We previously had 20,
// twice the source value. With TANK_FULL_ARMOUR = 40 a mine should take
// off a quarter of armour, not a half.
export const MINE_DAMAGE = 10;
// Live-trigger footprint: tank center within half a tile of the mine
// center registers as "on the mine" and queues an explosion. Smaller
// than the blast radius so the chain stays predictable. This is the
// circular contact test, not the explosion AoE.
export const MINE_TRIGGER_RADIUS = TILE_SIZE * 0.5;
// Blast radius. WinBolo tank.c:2265 — when a mine explodes, every tank
// whose center is within 384 world units (= 1.5 tiles = 24 px) on BOTH
// axes takes MINE_DAMAGE. Axis-aligned box, not a circle, so a tank
// catty-corner to the blast still gets clipped.
export const MINE_BLAST_HALF_PX = Math.round(1.5 * TILE_SIZE);
// Chain-reaction stagger. WinBolo minesexp.h MINES_EXPLOSION_WAIT = 10 ticks
// @ ~17 Hz (~590 ms); at our 20 Hz, 12 ticks ≈ 600 ms keeps wall-clock parity.
export const MINE_EXPLOSION_WAIT_TICKS = 12;
// Crater→water spread. WinBolo floodfill.h FLOOD_FILL_WAIT = 16 ticks
// @ ~17 Hz (~940 ms); 19 ticks @ 20 Hz ≈ 950 ms.
export const FLOOD_FILL_WAIT_TICKS = 19;

// ---------- Walls ----------
// building.h:43 BUILDING_LIFE = 4. A full wall absorbs 4 shell hits before
// becoming Rubble. After 2 hits the visible tile flips to HalfBuiltWall
// (so the wall has 2 hp remaining at HalfBuiltWall state); 4 hits total
// in any combination collapses it.
export const WALL_MAX_HP = 4;                      // shell hits before rubble
// Builder action costs from lgm.h (LGM_COST_*).
export const BUILDER_COST_WALL_TREES = 2;          // LGM_COST_BUILDING (lgm.h:50)
export const BUILDER_COST_ROAD_TREES = 2;          // LGM_COST_ROAD (lgm.h:49)
export const BUILDER_COST_BOAT_TREES = 20;         // LGM_COST_BOAT (lgm.h:52)
// LGM_COST_REPAIRBUILDING (lgm.h:51): repairing a HalfBuiltWall back to
// a full Wall costs 1 tree. Triggered when the player asks for a wall on
// a half-built tile (lgm.c:399-405).
export const BUILDER_COST_REPAIR_WALL_TREES = 1;
// lgm.c:426-445 — builder repair of a pillbox is a full-heal whose tree
// cost depends on the current armour: heavier damage costs more trees.
// We multiply LGM_COST_PILLREPAIR = 1 by 1/2/3/4 by armour tier:
//   armour >= 11        -> 1 tree
//   7 <= armour <= 10   -> 2 trees
//   3 <= armour <= 6    -> 3 trees
//   armour < 3          -> 4 trees
// The result is always armour = PILLBOX_MAX_ARMOR after the action.
export const BUILDER_COST_PILL_REPAIR_TREES = 1;   // LGM_COST_PILLREPAIR base unit (lgm.h:54)
// lgm.h:53 LGM_COST_PILLNEW = 4. Deploying a carried pillbox to a new
// tile costs 4 trees (in addition to the carried pill).
export const BUILDER_COST_PILLNEW_TREES = 4;
export const BUILDER_COST_MINE = 1;                // LGM_COST_MINE (1 mine consumed) (lgm.h:55)
export const BUILDER_HARVEST_TREES = 4;            // LGM_GATHER_TREE (lgm.h:59)
// Backwards-compat alias kept while we migrate any inbound references.
export const WALL_BUILD_COST_TREES = BUILDER_COST_WALL_TREES;
export const TANK_MAX_TREES_CAP = 40;

// ---------- Builder ----------
// Top speed (road / grass / boat / base). Terrain multipliers in
// shared/src/map.ts BUILDER_TERRAIN_SPEED scale this down on forest,
// swamp, rubble, crater. WinBolo bolo_map.h:89-102 — MAP_MANSPEED_TROAD
// = 16 wu/wb-tick, which converts to 2.5 px/our-tick at 20 Hz.
export const BUILDER_SPEED = 2.5;                  // px/tick on road/grass/boat
// lgm.h:77 LGM_BUILD_TIME = 20 wb-ticks @50fps = 400ms. At 20Hz: 8 ticks.
export const BUILDER_WORK_TICKS = 8;
export const BUILDER_RADIUS = 3;
export const BUILDER_RESPAWN_TICKS = 100;          // legacy 5s respawn (parachute supersedes)
export const BUILDER_MAX_DISTANCE_TILES = 8;       // can't deploy too far

// lgm.h LGM_HELICOPTER_SPEED in winbolo is 3 world units per wb-tick.
// TANK_SHIFT_MAPSIZE=8 makes one tile = 256 world units, so the authentic
// rate is 3/256 tile per 50Hz tick = ~0.59 tile/sec ≈ 0.47 px/tick at our
// 20Hz × 16-px tiles. That's so slow players would rage at the wait, so
// we run it at 3 px/tick instead — fast enough to clear a quarter-map
// flight in ~15 seconds, slow enough that the helicopter is unmistakably
// visible crossing the map and losing your builder genuinely costs you a
// chunk of the next minute. (Previously 8 px/tick — visually that read
// as "TLM teleports back" because a short flight was over before the
// player could even spot the chopper.)
export const LGM_HELICOPTER_SPEED_PX = 3;          // helicopter travel speed

// Builder stuck-detection constants. While in going/returning, sample the
// builder's position every BUILDER_STUCK_SAMPLE_TICKS; if the net
// displacement over the window is below BUILDER_STUCK_THRESHOLD_PX, count
// it as a no-progress sample. Reaching BUILDER_STUCK_GIVE_UP consecutive
// no-progress samples (≈ a few seconds of total stuckness) means the LGM
// is wedged on geometry (sliding along a wall, oscillating around a
// corner, …) — he abandons the trip and either heads home or, if already
// returning and wedged, snaps home with a refund.
export const BUILDER_STUCK_SAMPLE_TICKS = 40;     // 2 s @ 20 Hz
export const BUILDER_STUCK_THRESHOLD_PX = 8;      // half a tile
export const BUILDER_STUCK_GIVE_UP = 3;            // 3 samples = ~6 s of no progress

// ---------- Pillbox carry ----------
export const TANK_MAX_PILLBOXES_CARRY = 3;

// ---------- Rooms ----------
export const MAX_ROOMS = 32;
export const DEFAULT_ROOM_ID = "main";
export const DEFAULT_ROOM_NAME = "Starter Map";
export const ROOM_NAME_MIN = 3;
export const ROOM_NAME_MAX = 32;

// ---------- Bases (capture) ----------
export const BASE_CAPTURE_DAMAGE_PER_SHELL = 1;

// ---------- Matches ----------
export const MATCH_DURATION_MS = 10 * 60 * 1000;   // 10 minutes
export const MATCH_END_GRACE_MS = 15 * 1000;       // 15s scoreboard before reset
export const CHAT_MAX_LENGTH = 200;

// Default Match Lobby countdown duration (seconds). Admins can override via
// /admin/warmup. 0 disables the warmup entirely — matchmade rooms boot straight
// into the live match.
export const DEFAULT_WARMUP_SECONDS = 15;
export const MAX_WARMUP_SECONDS = 60;

// Minimum Match Lobby time guaranteed to a fresh joiner (seconds). If a
// player connects to a matchmade room with fewer seconds left on the warmup
// countdown, the server bumps warmupEndsAt so they get at least this many
// seconds in the lobby. 0 disables the floor. Admin-tunable via
// /admin/min-lobby.
export const DEFAULT_MIN_LOBBY_SECONDS = 5;
export const MAX_MIN_LOBBY_SECONDS = 30;

// ---------- Teams ----------
// Team 0 = free-for-all; each tank is its own faction and only its own
// player id counts for ownership. 1..MAX_TEAM are real team slots.
// Rooms pick a `teamCount` between 0 (FFA) and MAX_TEAM at creation time.
export const MAX_TEAM = 8;
export const DEFAULT_TEAM_COUNT = 2;
// Tank hull colors by team slot (index 0 = FFA fallback / no team).
export const TEAM_COLORS: readonly string[] = [
  "#3a6ac0", // team 0 (enemy-in-FFA default; overridden for self tank)
  "#e74c3c", // 1 red
  "#3498db", // 2 blue
  "#2ecc71", // 3 green
  "#f1c40f", // 4 yellow
  "#9b59b6", // 5 purple
  "#e67e22", // 6 orange
  "#1abc9c", // 7 teal
  "#ecf0f1", // 8 white
];

// ---------- Game style tuning ----------
// CLASSIC mirrors WinBolo (the constants above). TURBO scales speed,
// acceleration, capacity, and reload for a faster, more action-packed
// match. The shell at 5px/tick still cannot tunnel a 16px tile in one
// step, and 5px/tick stays under the TILE_SIZE collision broadphase
// guard, so the bumped speed is safe.
// SIEGE is a wave-survival mode: the player gets a prep window to stockpile,
// then faces escalating waves of relentless AI tanks. It reuses CLASSIC
// physics tuning; everything that makes it "siege" is the wave engine and
// room lifecycle, not the chassis numbers.
// BATTLE_ROYALE shares TURBO's chassis tuning (and stays linked to it via
// tankTuningFor — see below). Its identity is the shrinking safe-zone +
// health-draining fog engine and the last-tank-standing lifecycle (see the
// BR_* constants and BattleRoyaleState below). It runs as a matchmade room so
// it gets the same FFA-style Match Lobby (skin/colour picking, bot fill) as
// FREE FOR ALL.
export type GameStyle = "classic" | "turbo" | "siege" | "battle_royale";
export const DEFAULT_GAME_STYLE: GameStyle = "classic";

// ---------- SIEGE mode ----------
// Sentinel room id for the always-present "single-player challenge" entry the
// lobby renders at the top. It is NOT a real room — clicking it spins up a
// fresh private (hidden) siege room for that player; this id only tags the
// synthetic lobby row.
export const SIEGE_CHALLENGE_ROOM_ID = "siege-challenge";
// Prep window before the first wave: the player roams and stockpiles.
export const SIEGE_PREP_MS = 60 * 1000;
// Max length of a single wave. When it elapses, any enemies still alive are
// blown up and the next round begins. Per-round so the difficulty ramp gets
// a longer clock once the wave counts (and enemy density) reach the late
// game — see siegeWaveMs below for the actual round → ms mapping.
export const SIEGE_WAVE_MS = 2 * 60 * 1000;
// Round at and beyond which the wave clock extends to the long limit. Set so
// the opening ten rounds use SIEGE_WAVE_MS and round 11+ uses SIEGE_LONG_WAVE_MS.
export const SIEGE_LONG_WAVE_FROM_ROUND = 11;
export const SIEGE_LONG_WAVE_MS = 3 * 60 * 1000;

// Per-round wave duration in milliseconds. Mirrors the SIEGE_WAVE_COUNTS
// idea of a stepped ramp: rounds 1..10 get the standard wave clock, then
// every round from SIEGE_LONG_WAVE_FROM_ROUND onward gets the long clock so
// the player has room to handle the bigger swarms.
export function siegeWaveMs(round: number): number {
  return round >= SIEGE_LONG_WAVE_FROM_ROUND ? SIEGE_LONG_WAVE_MS : SIEGE_WAVE_MS;
}
// Quiet gap between a cleared wave and the next.
export const SIEGE_BREAK_MS = 10 * 1000;
// How long the GAME OVER scoreboard lingers before a fresh run auto-starts.
export const SIEGE_GAMEOVER_MS = 10 * 1000;
// Explicit wave sizes for the opening rounds; beyond this the count grows
// 10% per round (rounded up). Rounds are 1-based.
export const SIEGE_WAVE_COUNTS: readonly number[] = [2, 4, 6, 10, 14, 20, 24, 28, 36];
export const SIEGE_WAVE_GROWTH = 1.1;
// Enemy tanks spawn at 20% armour, with full tree loads, and chase relentlessly.
export const SIEGE_ENEMY_ARMOR_FRAC = 0.2;
// From this round on, enemy tanks will bridge rivers to reach the player.
export const SIEGE_ROAD_BUILD_FROM_WAVE = 5;
// From this round on, enemy tanks will RAM a player sitting on a base —
// skipping the usual hold-and-fire distance and charging full-throttle so
// the tank-tank push physics shoves the parked defender off the recharge
// tile. Stops camping on a base from being a safe late-game strategy.
export const SIEGE_RAM_BASE_FROM_ROUND = 15;
// Hard ceiling on concurrent enemy tanks so very late rounds stay performant.
export const SIEGE_ENEMY_CAP = 60;
// Total live-tank budget for a siege world (players + enemies). Far above the
// normal 16-player cap so big waves fit.
export const SIEGE_MAX_TANKS = 80;
// The lobby pads the real "defenders online" count by a random 3..7 so the
// challenge never reads as deserted.
export const SIEGE_DEFENDER_PAD_MIN = 3;
export const SIEGE_DEFENDER_PAD_MAX = 7;

// Number of enemy tanks for a given 1-based round. Uses the explicit table for
// the opening rounds, then compounds 10% per round, clamped to the cap.
export function siegeWaveCount(round: number): number {
  if (round < 1) return 0;
  if (round <= SIEGE_WAVE_COUNTS.length) {
    return SIEGE_WAVE_COUNTS[round - 1] ?? 0;
  }
  let count = SIEGE_WAVE_COUNTS[SIEGE_WAVE_COUNTS.length - 1] ?? 0;
  for (let r = SIEGE_WAVE_COUNTS.length + 1; r <= round; r++) {
    count = Math.ceil(count * SIEGE_WAVE_GROWTH);
  }
  return Math.min(count, SIEGE_ENEMY_CAP);
}

// ---------- BATTLE ROYALE mode ----------
// A 16-tank last-one-standing match. Players (and bot fill) spawn spread out
// on boats around the island's shoreline, then a dense fog closes in toward a
// single randomly-chosen safe point, draining the health of anyone caught
// outside the shrinking circle. The match ends when one tank is left alive
// (or the schedule below fully collapses the zone).
//
// Total match length. The fog schedule (BR_STAGES) is timed against the
// elapsed match time, so this is the sum of every stage's duration.
export const BATTLE_ROYALE_MATCH_MS = 7 * 60 * 1000; // 7 minutes

// The final safe zone must contain at least this fraction of ground (non-water)
// tiles so the showdown happens on land rather than open sea.
export const BR_FINAL_ZONE_GROUND_FRAC = 0.5;

// Safe circle radius at area fraction 1.0 (match start), expressed as a
// fraction of the map's longer dimension. 0.5 puts the opening boundary right
// at the edge of the game world (the map's edge midpoints, with the corners
// already in fog) so there's no safe rim of open sea to hide on — the fog is
// biting from the very first stage.
export const BR_FULL_RADIUS_FRAC = 0.5;

// How many random land tiles to try when picking the fixed safe-zone centre
// before falling back to the best (most-ground) candidate seen.
export const BR_ZONE_PICK_ATTEMPTS = 600;

// Extra radius (in tiles) added beyond the farthest spawn when computing the
// opening circle, so nobody starts already clipped by the fog.
export const BR_SPAWN_MARGIN_TILES = 10;

// Minimum spacing (in tiles) between shoreline spawn points so tanks don't
// start within brawling distance of each other.
export const BR_SPAWN_MIN_SEPARATION_TILES = 6;

// One shrink stage of the fog. Times are elapsed-match milliseconds. The safe
// circle's *area* is interpolated linearly from startAreaFrac → endAreaFrac
// across [startMs, endMs); the radius is derived from that area against the
// map area. drainFracPerSec is the share of a tank's max armour drained per
// second while it sits outside the safe circle during this stage.
export interface BattleRoyaleStage {
  startMs: number;
  endMs: number;
  startAreaFrac: number;
  endAreaFrac: number;
  drainFracPerSec: number;
}

// Four shrinking stages plus a sudden-death collapse, paced for a brisk
// ~7-minute match. Area fractions are of the whole map; drain ramps from a
// gentle nudge to an instant death sentence so passive late play is punished.
export const BR_STAGES: readonly BattleRoyaleStage[] = [
  // Stage 1 — The Initial Squeeze (0–1.5m): fog rolls in from the edges.
  { startMs: 0,             endMs: 90_000,  startAreaFrac: 1.0,  endAreaFrac: 0.60,  drainFracPerSec: 0.01 },
  // Stage 2 — Sector Isolation (1.5–3m).
  { startMs: 90_000,        endMs: 180_000, startAreaFrac: 0.60, endAreaFrac: 0.35,  drainFracPerSec: 0.03 },
  // Stage 3 — The Fortress Breaker (3–4.5m).
  { startMs: 180_000,       endMs: 270_000, startAreaFrac: 0.35, endAreaFrac: 0.15,  drainFracPerSec: 0.07 },
  // Stage 4 — The Final Arena (4.5–6m).
  { startMs: 270_000,       endMs: 360_000, startAreaFrac: 0.15, endAreaFrac: 0.05,  drainFracPerSec: 0.15 },
  // Sudden Death (6–7m): the circle collapses to nothing.
  { startMs: 360_000,       endMs: 420_000, startAreaFrac: 0.05, endAreaFrac: 0.0,   drainFracPerSec: 0.25 },
] as const;

// Resolves the active stage index for an elapsed-match time. Clamps to the
// last stage once the schedule is exhausted so the fog stays lethal.
export function brStageAt(elapsedMs: number): number {
  for (let i = 0; i < BR_STAGES.length; i++) {
    const s = BR_STAGES[i]!;
    if (elapsedMs < s.endMs) return i;
  }
  return BR_STAGES.length - 1;
}

// The safe circle's area fraction (of the whole map) at an elapsed-match time,
// linearly interpolated within the active stage.
export function brAreaFracAt(elapsedMs: number): number {
  const i = brStageAt(elapsedMs);
  const s = BR_STAGES[i]!;
  const span = s.endMs - s.startMs;
  const t = span > 0 ? Math.min(1, Math.max(0, (elapsedMs - s.startMs) / span)) : 1;
  return s.startAreaFrac + (s.endAreaFrac - s.startAreaFrac) * t;
}

export interface TankTuning {
  maxSpeed: number;
  accel: number;
  startShells: number;
  startArmor: number;
  maxShells: number;
  maxArmor: number;
  fireCooldownTicks: number;
  // Turn rate in bradians/tick. Classic = 2.5 (matches WinBolo's
  // 1.0 brad/wb-tick scaled to our 20 Hz tick). TURBO bumps it so the
  // faster chassis still feels steerable.
  turnRate: number;
  // Drift lerp factor: each tick, the tank's velocity-direction lerps
  // toward its facing-direction by this fraction (when above the
  // high-speed threshold). 1.0 = snap, no drift (classic). Lower = more
  // drift (turbo). Combined with the threshold, this creates the "slide
  // sideways when you whip the wheel at full speed" feel without
  // affecting low-speed handling.
  driftLerp: number;
  // Deceleration when the throttle demands a lower speed than the tank is
  // doing (px/tick²). Classic keeps the historical TANK_BRAKE; faster
  // chassis scale it up so stopping distance stays proportionate to speed.
  brake: number;
  // Coasting deceleration with the throttle released (px/tick²). Same
  // scaling rationale as brake.
  friction: number;
  // Displacement per tick while shell impact is shoving the tank
  // (px/tick, over TANK_SLIDE_TICKS). Scales with chassis speed so a hit
  // in TURBO shoves proportionally as far as it does in classic.
  slidePxPerTick: number;
  // Shell muzzle velocity (px/tick). Classic keeps the historical
  // SHELL_SPEED (2:1 vs tank top speed). TURBO's faster chassis needs a
  // faster shell or dodging becomes trivial — but shell TTL is derived
  // from gunsight length, so range stays gunsight-accurate at any speed
  // (see sightLenToTtl in the server physics).
  shellSpeed: number;
  // Damage one shell deals to a TANK. Structures (pillboxes, bases) keep
  // their own per-shell constants in every mode. TURBO pairs a heavier
  // shell with a slower fire rate: same shots-to-kill as classic
  // (8 hits), punchier per-shot feel, far fewer shells in flight.
  shellDamage: number;
}

export const CLASSIC_TUNING: TankTuning = {
  maxSpeed: TANK_MAX_SPEED,
  accel: TANK_ACCEL,
  startShells: TANK_START_SHELLS,
  startArmor: TANK_START_ARMOR,
  maxShells: TANK_MAX_SHELLS,
  maxArmor: TANK_MAX_ARMOR,
  fireCooldownTicks: TANK_FIRE_COOLDOWN_TICKS,
  turnRate: 2.5,
  driftLerp: 1.0,
  brake: TANK_BRAKE,
  friction: TANK_FRICTION,
  slidePxPerTick: TANK_SLIDE_PX_PER_TICK,
  shellSpeed: SHELL_SPEED,
  shellDamage: SHELL_DAMAGE,
};

export const TURBO_TUNING: TankTuning = {
  maxSpeed: TANK_MAX_SPEED * 1.6,          // 2.5 -> 4.0 px/tick (was 2x; 1.6x feels less twitchy)
  accel: TANK_ACCEL * 1.6,                 // 0.2 -> 0.32 (matches the speed bump)
  startShells: Math.round(TANK_START_SHELLS * 1.5), // 40 -> 60
  startArmor: Math.round(TANK_START_ARMOR * 1.5),   // 40 -> 60
  maxShells: Math.round(TANK_MAX_SHELLS * 1.5),     // 40 -> 60
  maxArmor: Math.round(TANK_MAX_ARMOR * 1.5),       // 40 -> 60
  fireCooldownTicks: TANK_FIRE_COOLDOWN_TICKS,       // 5 (250 ms) — classic cadence; the "super" feel comes from
                                                     // heavier, faster shells, not a bullet stream
  turnRate: 3.75,                                    // 2.5 -> 3.75 (1.5x; chassis stays responsive at high speed)
  driftLerp: 0.20,                                   // velocity catches up to heading at 20%/tick above the drift threshold
  brake: TANK_BRAKE * 1.6,                           // 0.5 -> 0.8: stopping distance scales with the 1.6x chassis speed
  friction: TANK_FRICTION * 1.6,                     // 0.15 -> 0.24: coasting decay matches the speed bump
  slidePxPerTick: TANK_SLIDE_PX_PER_TICK * 1.6,      // 2.5 -> 4.0: shell shove proportional to the faster chassis
  shellSpeed: SHELL_SPEED * 1.5,                     // 5.0 -> 7.5: ~1.9:1 vs the 4.0 chassis (classic is 2:1), so
                                                     // dodging is a skill again instead of a stroll
  shellDamage: 8,                                    // 5 -> 8: 60 armor / 8 = same 8 hits-to-kill as classic 40/5;
                                                     // sustained TTK ~1.9 s vs the old spam build's ~1.8 s
};

// SIEGE plays on classic chassis tuning — the mode's identity comes from the
// wave engine, not the tank numbers. Cloned (not aliased) so a future siege
// rebalance can't silently change classic, and vice versa.
export const SIEGE_TUNING: TankTuning = { ...CLASSIC_TUNING };

// BATTLE ROYALE deliberately starts from TURBO's chassis values — the mode
// was designed around the faster tank. Cloned so a turbo rebalance is an
// explicit decision for battle royale rather than a silent carry-over (the
// fog drain scales off maxArmor, so armor changes reshape fog lethality).
export const BATTLE_ROYALE_TUNING: TankTuning = { ...TURBO_TUNING };

export function tankTuningFor(style: GameStyle): TankTuning {
  if (style === "turbo") return TURBO_TUNING;
  if (style === "battle_royale") return BATTLE_ROYALE_TUNING;
  if (style === "siege") return SIEGE_TUNING;
  return CLASSIC_TUNING;
}

